/**
 * The provider registry.
 *
 * WHAT THIS FILE ACTUALLY PREVENTS
 * --------------------------------
 * The product requirement is: do not show a provider that does not work. The
 * obvious way to violate that is to hardcode a list of provider names in a
 * dropdown — which is a list of promises, not a list of implementations, and
 * every entry on it is a support ticket waiting to happen.
 *
 * So the list is derived from what is REGISTERED, and registration is only
 * possible by passing a real object that satisfies a provider interface. There
 * is no way to add a name without an implementation behind it, and `list()` is
 * the single source the UI reads. A provider that does not compile cannot be
 * offered, and one that has not passed the contract suite should not be
 * registered at all.
 *
 * Note what is NOT here: 'google-drive', 'onedrive', 'dropbox', 's3', 'webdav'
 * and 'dnr-cloud' are valid `ProviderId` values — the type system reserves them
 * so a future connector is a natural fit — but none of them is registered, and
 * this file makes no claim that any of them works.
 */
import type { ProviderId } from '@domain/sync/changeModel';
import type { BackupProvider, ProviderRegistration, SyncProvider } from './types';
import { FakeBackupProvider, FakeSyncProvider } from './fakeProvider';
import { supabaseSyncProvider } from './supabaseProvider';

export class ProviderRegistry {
  private readonly registrations = new Map<ProviderId, ProviderRegistration>();

  /**
   * Register a provider.
   *
   * Re-registering an id REPLACES it, and that is deliberate: tests swap the
   * fake for a variant without the registry accumulating stale entries, and a
   * duplicate id silently resolving to the first registration is a bug that
   * would only show up in production.
   */
  register(registration: ProviderRegistration): void {
    this.registrations.set(registration.id, registration);
  }

  /** Only ids that are registered. The UI's entire provider list. */
  list(kind?: 'sync' | 'backup'): ProviderRegistration[] {
    const all = [...this.registrations.values()];
    return kind ? all.filter((r) => r.kinds.includes(kind)) : all;
  }

  has(id: ProviderId): boolean {
    return this.registrations.has(id);
  }

  /**
   * Get a sync provider, or null when the id is not registered.
   *
   * Returning null rather than throwing is deliberate: "this provider is not
   * available here" is a normal answer the settings screen handles by saying
   * so, not an exception that should stop the app from starting.
   */
  sync(id: ProviderId): SyncProvider | null {
    const registration = this.registrations.get(id);
    if (!registration || !registration.kinds.includes('sync')) return null;
    const provider = registration.create();
    return 'pushChanges' in provider ? (provider as SyncProvider) : null;
  }

  /** Get a backup provider, or null. */
  backup(id: ProviderId): BackupProvider | null {
    const registration = this.registrations.get(id);
    if (!registration || !registration.kinds.includes('backup')) return null;
    const provider = registration.create();
    return 'uploadBackup' in provider ? (provider as BackupProvider) : null;
  }
}

/**
 * The registry the running application uses.
 *
 * TWO providers are registered: the managed DNR Vyapar connector, which is what
 * the Sync & Cloud settings screen offers, and the in-memory fake, which is
 * what the contract suite runs against. The fake stays registered in the
 * product build on purpose — the sync engine wiring is exercised on every run
 * rather than only in tests. It is NOT a cloud service, it holds no user data
 * beyond the tab, and the UI labels it as a diagnostic, not as cloud sync.
 */
export const providerRegistry = new ProviderRegistry();

providerRegistry.register({
  id: 'supabase',
  label: 'DNR Cloud Sync',
  kinds: ['sync'],
  model: 'Managed row store. Business sync data is protected by account ownership and row-level security.',
  create: () => supabaseSyncProvider,
});

providerRegistry.register({
  id: 'fake',
  label: 'In-memory (diagnostic only — not a cloud service)',
  kinds: ['sync', 'backup'],
  model: 'In-process memory. Data is lost when the app closes.',
  create: () => new FakeSyncProvider(),
});

// The backup side gets its own registration because it is a different object
// with a different capability set — the same reason `sync` and `backup` are
// separate interfaces. A provider offering only one of the two is normal.
providerRegistry.register({
  id: 'memory',
  label: 'In-memory backup target (diagnostic only)',
  kinds: ['backup'],
  model: 'In-process memory. Not durable, not a cloud service.',
  create: () => new FakeBackupProvider('memory'),
});
